/**
 * Settings (SPEC §3.2): editor behaviour (synced through the settings node, D45), who "me" is,
 * password, passkeys. Export and import arrive with M8.
 */
import { startRegistration, type PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser'
import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { EDITOR_OPTION_LABELS, exportJson, exportMarkdown, type EditorOptions, type ImportReport } from '@solar/core'
import { api, type ImportResult, type PasskeyInfo } from '../client/api.ts'
import { zip } from '../client/zip.ts'
import { dayTitle, todayKey } from './dates.ts'
import { useStore, useUi } from './hooks.ts'
import { navigate } from './router.ts'

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="settings-section">
      <h2>{title}</h2>
      {children}
    </section>
  )
}

function EditorOptionsForm() {
  const store = useStore()
  useUi()
  return (
    <>
      {(Object.keys(EDITOR_OPTION_LABELS) as (keyof EditorOptions)[]).map((key) => {
        const spec = EDITOR_OPTION_LABELS[key]
        return (
          <fieldset key={key} className="option">
            <legend>{spec.title}</legend>
            {spec.values.map(([value, label], i) => (
              <label key={value}>
                <input
                  type="radio"
                  name={key}
                  checked={store.options[key] === value}
                  onChange={() => store.setOption(key, value as never)}
                />{' '}
                {label}
                {i === 0 && <span className="muted"> (default)</span>}
              </label>
            ))}
          </fieldset>
        )
      })}
      <p className="muted">These follow you to every device.</p>
    </>
  )
}

function Me() {
  const store = useStore()
  useUi()
  const me = store.me ? store.g.get(store.me) : undefined
  return me ? (
    <p>
      You are{' '}
      <a href={`/n/${me.id}`} onClick={(e) => (e.preventDefault(), navigate(`/n/${me.id}`))}>
        {me.title}
      </a>
      . <span className="muted">`@me` in queries and the open-task strip mean this person.</span>
    </p>
  ) : (
    <p className="muted">Nobody yet. Open your person page and tick “This is me”; `@me` and the open-task strip use it.</p>
  )
}

function PasswordForm() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    if (next !== again) return setMsg({ ok: false, text: 'The new passwords differ.' })
    try {
      await api.changePassword(current, next)
      setCurrent('')
      setNext('')
      setAgain('')
      setMsg({ ok: true, text: 'Password changed. Other devices sign in again.' })
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : String(err) })
    }
  }
  return (
    <form className="settings-form" onSubmit={submit}>
      <label>
        Current password
        <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
      </label>
      <label>
        New password (12 characters or more)
        <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
      </label>
      <label>
        New password again
        <input type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} />
      </label>
      <button disabled={!current || next.length < 12 || !again}>Change password</button>
      {msg && <p className={msg.ok ? 'ok' : 'error'} role="status">{msg.text}</p>}
    </form>
  )
}

function Passkeys() {
  const [list, setList] = useState<PasskeyInfo[] | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const load = (): void => void api.passkeys().then((r) => setList(r.passkeys), () => setList([]))
  useEffect(load, [])
  const add = async (): Promise<void> => {
    setMsg(null)
    try {
      const optionsJSON = (await api.passkeyRegistrationOptions()) as PublicKeyCredentialCreationOptionsJSON
      const response = await startRegistration({ optionsJSON })
      const device = /Android/.test(navigator.userAgent) ? 'Android phone' : /iPhone|iPad/.test(navigator.userAgent) ? 'iPhone' : /Mac/.test(navigator.userAgent) ? 'Mac' : 'This device'
      await api.addPasskey(response, `${device}, ${dayTitle(new Date().toISOString().slice(0, 10)).title}`)
      load()
    } catch (err) {
      const name = err instanceof Error ? err.name : ''
      if (name !== 'NotAllowedError' && name !== 'AbortError') setMsg(err instanceof Error ? err.message : String(err))
    }
  }
  return (
    <>
      {list === null ? (
        <p className="muted">Loading…</p>
      ) : list.length === 0 ? (
        <p className="muted">No passkey yet. With one, the phone signs in with a fingerprint instead of the password.</p>
      ) : (
        <ul className="passkeys">
          {list.map((p) => (
            <li key={p.id}>
              <span>{p.name}</span>
              <span className="muted">{p.lastUsedAt ? `used ${dayTitle(new Date(p.lastUsedAt).toISOString().slice(0, 10)).title}` : 'not used yet'}</span>
              <button className="link" onClick={() => void api.removePasskey(p.id).then(load)} aria-label={`Remove ${p.name}`}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {'PublicKeyCredential' in window && (
        <button className="secondary" onClick={() => void add()}>
          Add a passkey on this device
        </button>
      )}
      {msg && <p className="error">{msg}</p>}
    </>
  )
}

/** Everything as Markdown (Logseq opens it) and JSON, built from the mirror: works offline too. */
function ExportSection() {
  const store = useStore()
  const [busy, setBusy] = useState(false)
  const run = async (): Promise<void> => {
    setBusy(true)
    try {
      const enc = new TextEncoder()
      const files = exportMarkdown(store.g, store.tagEnv).map((f) => ({ path: f.path, data: enc.encode(f.content) }))
      files.push({ path: 'solar.json', data: enc.encode(exportJson(store.g, Date.now())) })
      const bytes = await zip(files)
      const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/zip' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `solar-export-${todayKey()}.zip`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 30_000)
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <p className="muted">Every day and page as Markdown files that Logseq opens, and everything as JSON.</p>
      <button className="secondary" disabled={busy} onClick={() => void run()}>
        {busy ? 'Preparing…' : 'Export everything'}
      </button>
    </>
  )
}

function ReportView({ r }: { r: ImportReport }) {
  const rows: [string, number][] = [
    ['days', r.journalDays],
    ['pages', r.pages],
    ['blocks', r.blocks],
    ['people', r.persons],
    ['memories', r.memories],
    ['chats', r.conversations],
    ['chat messages', r.messages],
    ['fields from block properties', r.properties],
    ['attachments (the files follow in Phase 2)', r.assetEmbeds],
    ['recordings (title and transcript kept as text)', r.recordings],
    ['links to v1 day titles kept as plain dates', r.journalLinksAsText],
    ['blocks without a parent, put under their page', r.orphanBlocks],
    ['long blocks split', r.splitBlocks],
    ['already imported, left alone', r.skippedExisting],
  ]
  return (
    <div className="report">
      <ul>
        {rows
          .filter(([, n]) => n > 0)
          .map(([label, n]) => (
            <li key={label}>
              <b>{n}</b> {label}
            </li>
          ))}
      </ul>
      {r.renamedTitles.length > 0 && (
        <details>
          <summary>{r.renamedTitles.length} titles changed to fit</summary>
          <ul>
            {r.renamedTitles.map(([a, b]) => (
              <li key={a}>
                {a} → {b}
              </li>
            ))}
          </ul>
        </details>
      )}
      {r.mergedPages.length > 0 && (
        <details>
          <summary>{r.mergedPages.length} pages joined with a page of the same name</summary>
          <ul>
            {r.mergedPages.map(([a, b], i) => (
              <li key={`${a}${i}`}>
                {a} → {b}
              </li>
            ))}
          </ul>
        </details>
      )}
      {r.createdForLinks.length > 0 && (
        <details>
          <summary>{r.createdForLinks.length} pages created for links that had none</summary>
          <p className="muted">{r.createdForLinks.join(', ')}</p>
        </details>
      )}
    </div>
  )
}

/** Migration from Solar v1 (SPEC §15): the file from scripts/v1-extract.mjs; dry run first. */
function ImportSection() {
  const [dump, setDump] = useState<unknown>(null)
  const [plan, setPlan] = useState<ImportResult | null>(null)
  const [done, setDone] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const choose = async (file: File | undefined): Promise<void> => {
    setError(null)
    setPlan(null)
    setDone(null)
    if (!file) return
    setBusy(true)
    try {
      const parsed = JSON.parse(await file.text()) as unknown
      setDump(parsed)
      setPlan(await api.importV1(parsed, true))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }
  const run = async (): Promise<void> => {
    setBusy(true)
    try {
      setDone(await api.importV1(dump, false))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <p className="muted">
        Choose the file <code>scripts/v1-extract.mjs</code> made from the v1 database. You see what would happen before anything changes; running it again later adds only what is new.
      </p>
      <input type="file" accept=".json,application/json" aria-label="v1 export file" disabled={busy} onChange={(e) => void choose(e.target.files?.[0])} />
      {plan && !done && (
        <>
          <ReportView r={plan.report} />
          <button className="secondary" disabled={busy || plan.ops === 0} onClick={() => void run()}>
            {plan.ops === 0 ? 'Nothing new to import' : busy ? 'Importing…' : 'Import'}
          </button>
        </>
      )}
      {done && (
        <p className={done.rejected.length ? 'error' : 'ok'} role="status">
          {done.rejected.length ? `Imported with ${done.rejected.length} problems: ${done.rejected.slice(0, 3).join('; ')}` : `Imported: ${done.ops} changes.`}
        </p>
      )}
      {error && <p className="error">{error}</p>}
    </>
  )
}

export function Settings() {
  return (
    <div className="scroller">
      <div className="column settings">
        <h1 className="settings-title">Settings</h1>
        <Section title="Editor">
          <EditorOptionsForm />
        </Section>
        <Section title="You">
          <Me />
        </Section>
        <Section title="Password">
          <PasswordForm />
        </Section>
        <Section title="Passkeys">
          <Passkeys />
        </Section>
        <Section title="Export">
          <ExportSection />
        </Section>
        <Section title="Import from Solar v1">
          <ImportSection />
        </Section>
      </div>
    </div>
  )
}
